import clsx from "clsx";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useSettings } from "../../context/SettingsContext";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

const ArrowIcon = ({ isOpen }: { isOpen: boolean }) => (
  <svg
    className={twMerge(
      "transition-transform duration-300 ml-2",
      isOpen ? "rotate-180" : "rotate-0"
    )}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.64645 6.64645C4.84171 6.45118 5.15829 6.45118 5.35355 6.64645L8 9.29289L10.6464 6.64645C10.8417 6.45118 11.1583 6.45118 11.3536 6.64645C11.5488 6.84171 11.5488 7.15829 11.3536 7.35355L8.35355 10.3536C8.15829 10.5488 7.84171 10.5488 7.64645 10.3536L4.64645 7.35355C4.45118 7.15829 4.45118 6.84171 4.64645 6.64645Z"
      fill="currentColor"
    />
  </svg>
);

interface AccordionProps {
  title: React.ReactNode;
  variant: "card" | "section";
  icon?: React.ReactNode;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
  className?: string;
  children: React.ReactNode;
}

// NOTE: If needed in the future, we can expose the contentRef to the parent
// by forwarding it via React.forwardRef or providing a callback prop like `onContentRef`.
// Example approaches:
// 1) Forward the internal contentRef by combining it with the forwarded ref using `useImperativeHandle`.
// 2) Accept a prop like `onContentRef` and call it with the contentRef.current to give parent access.
// This can enable controlling scroll position or measuring content height externally.

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      title,
      variant,
      icon,
      isOpen: controlledOpen,
      defaultOpen = false,
      onToggle,
      children,
      className = "",
    },
    ref
  ) => {
    // Controlled/uncontrolled open state
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isOpen =
      controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
    const [isRendered, setIsRendered] = useState(isOpen);

    // For animated height and scroll container reference.
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState("0px");
    const { oversizeWidget } = useSettings();

    useEffect(() => {
      if (isOpen) {
        setIsRendered(true);
      } else {
        // Unmount the component after the transition is complete
        const timeout = setTimeout(() => {
          setIsRendered(false);
        }, 600); // This duration should match the transition duration in CSS
        return () => clearTimeout(timeout);
      }
    }, [isOpen]);

    useEffect(() => {
      const el = contentRef.current;
      if (!el || !isRendered) return;

      if (isOpen) {
        // Step 1: Expand the accordion by setting the height to its scrollHeight
        const scrollHeight = el.scrollHeight;
        setHeight(`${scrollHeight}px`);

        // Step 2: After the transition completes, set height to "auto" for dynamic content resizing
        const timeout = setTimeout(() => {
          setHeight("auto");
        }, 600); // matches the transition duration in CSS

        // Clear timeout if the component unmounts or isOpen changes before the timer ends
        return () => clearTimeout(timeout);
      } else {
        // To collapse smoothly, we need to transition from a specific height to 0.
        // If height is 'auto', we first set it to the current scrollHeight.
        const scrollHeight = el.scrollHeight;
        setHeight(`${scrollHeight}px`);

        // Then, give the browser a moment to apply the new height before setting it to 0
        // to trigger the collapse animation.
        const timeout = setTimeout(() => {
          setHeight("0px");
        }, 15);

        return () => clearTimeout(timeout);
      }
    }, [isOpen, isRendered]);

    /**
     * Toggles the open state of the accordion.
     *
     * If the component is uncontrolled (no `controlledOpen` prop), it toggles internal state.
     * Regardless of controlled/uncontrolled mode, it calls `onToggle` callback if provided.
     */
    const handleToggle = () => {
      if (controlledOpen === undefined) {
        // For uncontrolled mode: toggle internal state
        setUncontrolledOpen((prev) => !prev);
      }

      // Notify parent component (if any) about the new open state
      onToggle?.(!isOpen);
    };

    return (
      <div
        ref={ref}
        className={twMerge(
          clsx(
            "px-2",
            variant === "card"
              ? "bg-white rounded-lg mx-2"
              : "mb-2 relative rounded-lg",
            className
          )
        )}
      >
        <div
          className={twMerge(
            clsx(
              "cursor-pointer",
              variant === "card"
                ? "flex justify-between items-center space-x-5 transition-all duration-700 ease-in-out p-2"
                : "w-full text-left border-[#E6E8F0] px-4 py-2 flex justify-between items-center focus:outline-none bg-transparent"
            )
          )}
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
        >
          <div
            className={clsx(
              "flex items-center space-x-2",
              variant === "card" ? "pl-1" : "pl-0"
            )}
          >
            {icon && <span>{icon}</span>}
            <span
              className={twMerge(
                clsx(
                  oversizeWidget ? "font-semibold" : undefined,
                  variant === "card"
                    ? "flex items-center text-[20px] font-[500px] text-black pl-2"
                    : "text-[20px] font-[500px] text-black"
                )
              )}
            >
              {title}
            </span>
          </div>
          {isOpen ? (
            <FaChevronUp
              className={twMerge(
                clsx(
                  "transition-transform duration-300",
                  "w-4 h-4"
                )
              )}
            />
          ) : (
            <FaChevronDown
              className={twMerge(
                clsx(
                  "transition-transform duration-300",
                  "w-4 h-4"
                )
              )}
            />
          )}
        </div>
        {isRendered && (
          <div
            ref={contentRef}
            className={twMerge(
              clsx(
                "overflow-hidden transition-all duration-700 ease-in-out",
                "p-2"
              )
            )}
            style={{
              height: height,
            }}
          >
            <div>{children}</div>
          </div>
        )}
      </div>
    );
  }
);

export default Accordion;
